import { BALANCE } from '../sim/balance';
import { shadow } from '../sim/guard-ai';
import { ITEMS } from '../sim/items';
import { encumbrance } from '../sim/inventory';
import type { PlayerState, RoundPhase } from '../sim/types';
import type { Consignment, ScoreLine } from '../sim/round';
import type { Target } from './interaction';

/**
 * THE HUD.
 *
 * DOM, not canvas. Three reasons and they all matter here: text stays crisp at
 * any resolution without a font atlas, layout is free, and — the one that
 * decided it — the screenshot harness photographs the whole page, so the HUD is
 * captured and reviewed by exactly the same pipeline as the mall itself.
 *
 * The visual rule is the same as the building's: this is signage inside a
 * corporate space, not a game overlay. Monospace, wide tracking, upper case,
 * nothing rounded, nothing that glows more than the mall does. The player should
 * half-believe the numbers are printed on the inside of a balaclava.
 *
 * Everything here is a pure render of state passed in. The HUD never reads the
 * round, never mutates it, and holds no game logic — so a bot client runs the
 * whole game with this file never loaded.
 */

export interface HudState {
  phase: RoundPhase;
  clock: number;
  player: PlayerState;
  target: Target | null;
  /** 0..1 hold progress on the current target. */
  progress: number;
  stamina: number;
  /** Set while the pointer is not captured. */
  paused: boolean;
  /** How many thieves are still in the building, including this one. */
  thievesLeft: number;
  /** Rising while a guard's camera or eyes are on you. 0..1. */
  detection: number;
  /**
   * How brightly the building is lighting you, 0..1. Null hides the readout
   * entirely — a guard has no use for it, and neither does a scene with no
   * light rig loaded.
   */
  exposure: number | null;
  /** Null on a machine with no microphone and nobody to hear. */
  voice: VoiceHud | null;
  /** Tonight's name and its delivery. Read once, during the briefing. */
  night: NightBrief | null;
  /** What to do next, and where. Null in the lobby and on the results screen. */
  objective: ObjectiveHud | null;
}

/**
 * The objective, already projected.
 *
 * The HUD holds no camera and does no maths on world space — the session hands
 * it a place on screen, because the session is the only thing that knows where
 * the player is looking. `screen` is null when the objective is a state of
 * affairs rather than a destination ("wait for the doors", "catch them").
 */
export interface ObjectiveHud {
  verb: string;
  detail: string;
  /** Bright red rather than dim blue. Set when the clock is the problem. */
  urgent: boolean;
  mark: string;
  /** Metres, straight line. */
  distance: number;
  screen: {
    /** 0..1 across the viewport, top-left origin. */
    x: number;
    y: number;
    /** True when the point is behind the player or past the frame, and the
     *  marker has been pinned to the edge as an arrow instead. */
    edge: boolean;
    /** Radians, screen space, for the edge arrow. 0 points right. */
    angle: number;
  } | null;
}

/**
 * THE BRIEFING CARD.
 *
 * Fifteen seconds of standing in a car park is dead time unless it is the moment
 * the crew decides what kind of night this is, and it can only be that if the
 * night is different. So the card says two things and stops: what this one is
 * called, and where the delivery landed. Everything else — the route, who takes
 * which tool, whether the pallet of televisions is worth two trips — is the
 * conversation the card exists to start, and it belongs in voice chat, not here.
 */
/** The three numbers and the name the results card adds to the table. */
export interface ResultsExtras {
  /** The night that just ended, so a good one can be typed back in. */
  name: string;
  /** Dollars still in the building when the shutters came down. */
  floor: number;
  /** Best single night on this machine, before tonight. */
  best: number;
  newBest: boolean;
}

export interface NightBrief {
  /** Four letters. Sayable out loud, and enough to name a night in a bug report. */
  name: string;
  consignment: Consignment | null;
}

/**
 * WHAT THE MICROPHONE IS DOING.
 *
 * Worth screen space for one reason: an open microphone you have forgotten
 * about is the single worst thing a game can do to somebody, and the only
 * defence is a light that is on when the microphone is. The talking list is the
 * other half — hearing a voice with no idea whose it is makes a room feel
 * haunted rather than occupied, and the names cost eight pixels.
 */
export interface VoiceHud {
  /** The microphone is open. False before the player has granted it. */
  live: boolean;
  muted: boolean;
  /** Own input level, 0..1. The proof that the meter is not just a green light. */
  level: number;
  /** Everyone being heard this instant, by name. */
  talking: string[];
}

export class Hud {
  private readonly root: HTMLDivElement;
  private readonly clockEl: HTMLDivElement;
  private readonly phaseEl: HTMLDivElement;
  private readonly bankEl: HTMLDivElement;
  private readonly carryEl: HTMLDivElement;
  private readonly slotsEl: HTMLDivElement;
  private readonly toolsEl: HTMLDivElement;
  private readonly weightFill: HTMLDivElement;
  private readonly weightText: HTMLDivElement;
  private readonly stamFill: HTMLDivElement;
  private readonly shadeBar: HTMLDivElement;
  private readonly shadeFill: HTMLDivElement;
  private readonly shadeText: HTMLDivElement;
  private readonly promptEl: HTMLDivElement;
  private readonly promptTitle: HTMLDivElement;
  private readonly promptDetail: HTMLDivElement;
  private readonly promptVerb: HTMLDivElement;
  private readonly ring: SVGCircleElement;
  private readonly ringWrap: HTMLDivElement;
  private readonly reticle: HTMLDivElement;
  private readonly logEl: HTMLDivElement;
  private readonly pauseEl: HTMLDivElement;
  private readonly resultsEl: HTMLDivElement;
  private readonly briefEl: HTMLDivElement;
  private readonly objEl: HTMLDivElement;
  private readonly objVerb: HTMLDivElement;
  private readonly objDetail: HTMLDivElement;
  private readonly markEl: HTMLDivElement;
  private readonly markArrow: HTMLDivElement;
  private readonly markPip: HTMLDivElement;
  private readonly markText: HTMLDivElement;
  /** The night already painted, so the card is built once and not every frame. */
  private briefShown: string | null = null;
  private readonly detectEl: HTMLDivElement;
  private readonly voiceEl: HTMLDivElement;
  private readonly micEl: HTMLDivElement;
  private readonly micFill: HTMLDivElement;
  private readonly micLabel: HTMLDivElement;
  private readonly talkEl: HTMLDivElement;
  private readonly toasts: Array<{ el: HTMLDivElement; until: number }> = [];
  private now = 0;

  constructor(parent: HTMLElement) {
    injectStyle();
    this.root = el('div', 'hud');
    parent.appendChild(this.root);

    // --- top: the clock ----------------------------------------------------
    const top = el('div', 'hud-top');
    this.phaseEl = el('div', 'hud-phase');
    this.clockEl = el('div', 'hud-clock');
    this.objVerb = el('div', 'o-verb');
    this.objDetail = el('div', 'o-detail');
    this.objEl = el('div', 'hud-objective');
    this.objEl.append(this.objVerb, this.objDetail);
    top.append(this.phaseEl, this.clockEl, this.objEl);
    this.root.appendChild(top);

    // The waypoint. One element that is either a diamond over the place or an
    // arrow pinned to the edge of the frame; never two, so it cannot show both.
    this.markEl = el('div', 'hud-mark');
    this.markArrow = el('div', 'm-arrow');
    this.markPip = el('div', 'm-pip');
    this.markText = el('div', 'm-text');
    this.markEl.append(this.markArrow, this.markPip, this.markText);
    this.root.appendChild(this.markEl);

    // --- top left: money ---------------------------------------------------
    const money = el('div', 'hud-money');
    this.bankEl = el('div', 'hud-bank');
    this.carryEl = el('div', 'hud-carry');
    money.append(this.bankEl, this.carryEl);
    this.root.appendChild(money);

    // --- centre: reticle, hold ring, prompt --------------------------------
    const centre = el('div', 'hud-centre');
    this.reticle = el('div', 'hud-reticle');
    this.ringWrap = el('div', 'hud-ring');
    this.ringWrap.innerHTML =
      '<svg viewBox="0 0 100 100"><circle class="track" cx="50" cy="50" r="44"/>' +
      '<circle class="fill" cx="50" cy="50" r="44"/></svg>';
    this.ring = this.ringWrap.querySelector('.fill') as SVGCircleElement;
    centre.append(this.reticle, this.ringWrap);
    this.root.appendChild(centre);

    this.promptEl = el('div', 'hud-prompt');
    this.promptTitle = el('div', 'p-title');
    this.promptDetail = el('div', 'p-detail');
    this.promptVerb = el('div', 'p-verb');
    this.promptEl.append(this.promptTitle, this.promptDetail, this.promptVerb);
    this.root.appendChild(this.promptEl);

    // --- bottom left: what you are carrying --------------------------------
    const carry = el('div', 'hud-inventory');
    this.slotsEl = el('div', 'hud-slots');
    this.toolsEl = el('div', 'hud-tools');
    carry.append(this.slotsEl, this.toolsEl);
    this.root.appendChild(carry);

    // --- bottom right: what it is doing to you -----------------------------
    const body = el('div', 'hud-body');
    // Concealment sits above weight because it changes faster than anything
    // else here and it is the one number you are allowed to fix by walking two
    // metres sideways. The word does the work; the bar is there so you can see
    // it moving before you have finished reading the word.
    this.shadeBar = el('div', 'bar shade');
    this.shadeFill = el('div', 'bar-fill');
    this.shadeText = el('div', 'bar-label');
    this.shadeBar.append(this.shadeFill, this.shadeText);
    const weight = el('div', 'bar weight');
    this.weightFill = el('div', 'bar-fill');
    this.weightText = el('div', 'bar-label');
    weight.append(this.weightFill, this.weightText);
    const stam = el('div', 'bar stamina');
    this.stamFill = el('div', 'bar-fill');
    const stamLabel = el('div', 'bar-label');
    stamLabel.textContent = 'WIND';
    stam.append(this.stamFill, stamLabel);
    body.append(this.shadeBar, weight, stam);
    this.root.appendChild(body);

    // --- top right: the microphone -----------------------------------------
    this.voiceEl = el('div', 'hud-voice');
    this.micEl = el('div', 'v-mic');
    this.micFill = el('div', 'v-level');
    this.micLabel = el('div', 'v-label');
    this.micEl.append(this.micFill, this.micLabel);
    this.talkEl = el('div', 'v-talking');
    this.voiceEl.append(this.micEl, this.talkEl);
    this.root.appendChild(this.voiceEl);

    this.detectEl = el('div', 'hud-detect');
    this.detectEl.textContent = 'SEEN';
    this.root.appendChild(this.detectEl);

    this.logEl = el('div', 'hud-log');
    this.root.appendChild(this.logEl);

    this.pauseEl = el('div', 'hud-pause');
    this.pauseEl.innerHTML = PAUSE_CARD;
    this.root.appendChild(this.pauseEl);

    this.resultsEl = el('div', 'hud-results');
    this.root.appendChild(this.resultsEl);
    this.briefEl = el('div', 'hud-brief');
    this.root.appendChild(this.briefEl);
  }

  /** A line in the running log. Kind drives the colour. */
  toast(text: string, kind: 'good' | 'bad' | 'info' = 'info', seconds = 4): void {
    const line = el('div', `toast ${kind}`);
    line.textContent = text;
    this.logEl.appendChild(line);
    this.toasts.push({ el: line, until: this.now + seconds });
    while (this.toasts.length > 6) {
      const old = this.toasts.shift();
      old?.el.remove();
    }
  }

  /** Hidden while the review camera owns the scene, so shots stay clean. */
  setVisible(on: boolean): void {
    this.root.style.display = on ? '' : 'none';
  }

  update(dt: number, s: HudState): void {
    this.now += dt;

    const warn = s.phase === 'active' && s.clock <= BALANCE.round.warnAtSec;
    this.clockEl.textContent = mmss(s.clock);
    this.clockEl.classList.toggle('warn', warn);
    this.phaseEl.textContent =
      s.phase === 'briefing'
        ? 'DOORS OPEN IN'
        : s.phase === 'active'
          ? `${s.thievesLeft} INSIDE`
          : s.phase === 'results'
            ? 'CLOSED'
            : 'LOBBY';

    this.renderBrief(s);
    this.renderObjective(s.objective);

    const p = s.player;
    const carried = p.carried.reduce((n, x) => n + (ITEMS[x.item]?.value ?? 0), 0);
    this.bankEl.textContent = `$${p.banked.toLocaleString()}`;
    this.carryEl.textContent = carried ? `+ $${carried.toLocaleString()} ON YOU` : 'NOTHING ON YOU';
    this.carryEl.classList.toggle('at-risk', carried > 0);

    // Slots. Rendered as fixed boxes rather than a list, because the empty ones
    // are the information — "two left" is the decision the player is making.
    const enc = encumbrance(p);
    this.renderSlots(p);
    this.weightFill.style.width = `${Math.min(100, enc.load * 100).toFixed(1)}%`;
    this.weightFill.className = `bar-fill ${enc.load > 0.75 ? 'hot' : enc.load > 0.45 ? 'warm' : ''}`;
    this.weightText.textContent = `${enc.kg.toFixed(1)}KG · ${enc.tier.toUpperCase()} · ${Math.round(
      enc.speed * 100,
    )}%`;
    this.stamFill.style.width = `${(s.stamina * 100).toFixed(1)}%`;
    this.renderShade(s.exposure);

    this.detectEl.style.opacity = s.detection > 0.02 ? String(Math.min(1, s.detection)) : '0';
    this.detectEl.classList.toggle('caught', s.detection >= 1);

    this.renderVoice(s.voice);

    // --- target ------------------------------------------------------------
    const t = s.target;
    this.reticle.classList.toggle('active', !!t && !t.blocked);
    this.reticle.classList.toggle('denied', !!t?.blocked);
    if (t) {
      this.promptEl.classList.add('on');
      this.promptTitle.textContent = t.title;
      this.promptDetail.textContent = t.detail;
      this.promptVerb.textContent = t.blocked ?? t.verb;
      this.promptVerb.className = `p-verb ${t.blocked ? 'blocked' : ''}`;
    } else {
      this.promptEl.classList.remove('on');
    }

    const showRing = s.progress > 0.001;
    this.ringWrap.classList.toggle('on', showRing);
    // 276.5 is the circumference of r=44. Drawn as a dash offset so it sweeps.
    this.ring.style.strokeDashoffset = String(276.5 * (1 - s.progress));

    this.pauseEl.classList.toggle('on', s.paused && s.phase !== 'results');

    for (let i = this.toasts.length - 1; i >= 0; i--) {
      if (this.toasts[i].until > this.now) continue;
      this.toasts[i].el.classList.add('gone');
      const [dead] = this.toasts.splice(i, 1);
      setTimeout(() => dead.el.remove(), 400);
    }
  }

  /**
   * The objective line, and the waypoint that saves it from being useless.
   *
   * Naming a store is worthless in a building the player has never walked. So
   * the line says the verb and the marker says the direction, and between them
   * a first-time player can be robbing the right shop inside thirty seconds
   * without anyone having explained the game to them.
   *
   * The marker is deliberately quiet — a hairline diamond and a distance, no
   * fill, no bounce. It has to survive being on screen for the entire round
   * without becoming the thing you look at instead of the mall.
   */
  private renderObjective(o: ObjectiveHud | null): void {
    this.objEl.classList.toggle('on', !!o);
    this.markEl.classList.toggle('on', !!o?.screen);
    if (!o) return;

    this.objVerb.textContent = o.verb;
    this.objVerb.classList.toggle('urgent', o.urgent);
    this.objDetail.textContent = o.detail;

    const sc = o.screen;
    if (!sc) return;
    this.markEl.classList.toggle('edge', sc.edge);
    this.markEl.style.left = `${(sc.x * 100).toFixed(2)}%`;
    this.markEl.style.top = `${(sc.y * 100).toFixed(2)}%`;
    // Rotated on the arrow alone: rotating the whole marker would take the
    // label with it and print the store name upside down behind you.
    this.markArrow.style.transform = `rotate(${sc.angle.toFixed(3)}rad)`;
    const metres = o.distance >= 10 ? Math.round(o.distance) : o.distance.toFixed(1);
    this.markText.textContent = o.mark ? `${o.mark} · ${metres}M` : `${metres}M`;
  }

  /**
   * THE CONCEALMENT READOUT.
   *
   * The number shown is the guard's notice rate as a percentage of its
   * unmodified self, taken from the same `shadow()` the guards run — not a
   * separate presentation curve. If those two ever disagreed the player would
   * be learning a rule the game does not enforce, which is worse than showing
   * nothing at all.
   *
   * Three words rather than a continuous number because that is how many
   * decisions there are: the dark is worth crossing the mall for, the half-dark
   * is worth not lingering in, and the light is worth being frightened of.
   */
  private renderShade(exposure: number | null): void {
    this.shadeBar.style.display = exposure === null ? 'none' : '';
    if (exposure === null) return;
    const mul = shadow(exposure);
    const lit = (mul - BALANCE.guard.shadowMul) / (1 - BALANCE.guard.shadowMul);
    this.shadeFill.style.width = `${(lit * 100).toFixed(1)}%`;
    this.shadeFill.className = `bar-fill ${lit > 0.8 ? 'hot' : lit > 0.45 ? 'warm' : ''}`;
    const word = lit < 0.22 ? 'HIDDEN' : lit < 0.72 ? 'SHADED' : 'LIT';
    this.shadeText.textContent = `${word} · SEEN ${Math.round(mul * 100)}%`;
  }

  private lastVoiceSignature = '';

  private renderVoice(v: VoiceHud | null): void {
    this.voiceEl.classList.toggle('on', !!v);
    if (!v) return;
    // The meter is the one part that has to move every frame — it is what turns
    // "the game claims the microphone is open" into "I can see it hearing me".
    this.micFill.style.width = `${Math.min(100, v.level * 260).toFixed(0)}%`;
    const sig = `${v.live}|${v.muted}|${v.talking.join(',')}`;
    if (sig === this.lastVoiceSignature) return;
    this.lastVoiceSignature = sig;
    this.micEl.className = `v-mic ${v.muted ? 'muted' : v.live ? 'live' : 'off'}`;
    this.micLabel.textContent = v.muted ? 'MIC OFF · B' : v.live ? 'MIC LIVE · B' : 'PRESS B FOR MIC';
    this.talkEl.replaceChildren();
    for (const name of v.talking) {
      const row = el('div', 'v-who');
      row.textContent = name.toUpperCase();
      this.talkEl.appendChild(row);
    }
  }

  private lastSlotSignature = '';

  private renderSlots(p: PlayerState): void {
    const sig = `${p.carried.map((s) => s.item).join(',')}|${p.tools.join(',')}`;
    if (sig === this.lastSlotSignature) return;
    this.lastSlotSignature = sig;

    this.slotsEl.replaceChildren();
    // Group identical items rather than printing eight lines of "Gold Rope
    // Chain" — the count is what the player is tracking.
    const counts = new Map<string, number>();
    for (const s of p.carried) counts.set(s.item, (counts.get(s.item) ?? 0) + 1);
    for (const [id, n] of counts) {
      const def = ITEMS[id];
      const row = el('div', `slot ${def.kind}`);
      row.innerHTML =
        `<span class="s-name">${escapeHtml(def.name)}${n > 1 ? ` ×${n}` : ''}</span>` +
        `<span class="s-val">${def.value ? `$${(def.value * n).toLocaleString()}` : def.kind.toUpperCase()}</span>`;
      this.slotsEl.appendChild(row);
    }
    const free = BALANCE.carry.itemSlots - p.carried.length;
    if (free > 0) {
      const row = el('div', 'slot empty');
      row.textContent = `${free} SLOT${free === 1 ? '' : 'S'} FREE`;
      this.slotsEl.appendChild(row);
    }

    this.toolsEl.replaceChildren();
    for (let i = 0; i < BALANCE.carry.toolSlots; i++) {
      const id = p.tools[i];
      const box = el('div', `tool ${id ? '' : 'empty'}`);
      box.textContent = id ? ITEMS[id].name.toUpperCase() : 'NO TOOL';
      this.toolsEl.appendChild(box);
    }
  }

  /**
   * Paints the briefing card, once, and takes it away when the doors open.
   *
   * Keyed on the night's name rather than a boolean so a restart mid-briefing —
   * which mints a new night under a HUD that is already showing one — repaints
   * instead of leaving yesterday's consignment on screen.
   */
  private renderBrief(s: HudState): void {
    const show = s.phase === 'briefing' && s.night;
    if (!show) {
      if (this.briefShown !== null) {
        this.briefEl.classList.remove('on');
        this.briefShown = null;
      }
      return;
    }
    const night = s.night!;
    if (this.briefShown === night.name) return;
    this.briefShown = night.name;

    const c = night.consignment;
    const body = c
      ? `<div class="b-label">${escapeHtml(c.label)}</div>` +
        `<div class="b-where">${escapeHtml(c.storeName)}</div>` +
        `<div class="b-what">${c.count} \u00d7 ${escapeHtml(ITEMS[c.item]?.name ?? c.item)}` +
        ` \u00b7 <b>$${c.value.toLocaleString()}</b>` +
        ` \u00b7 ${(c.count * (ITEMS[c.item]?.weight ?? 0)).toFixed(1)}KG</div>`
      : `<div class="b-label">NO DELIVERIES SCHEDULED</div>`;

    this.briefEl.innerHTML =
      `<div class="b-card"><div class="b-head">TONIGHT</div>` +
      `<div class="b-name">${escapeHtml(night.name)}</div>` +
      body +
      // The card was pure flavour and a first-time player could read all of it
      // without learning that loot in your hands scores nothing. One sentence,
      // in plain English, saying the actual shape of the round.
      `<div class="b-plain">Take two tools. Rob the place. Post what you carry down a chute` +
      ` for ${Math.round(BALANCE.dropoff.solo.payoutRate * 100)}%, or run it to the loading dock` +
      ` with a mate for full price. Walk out to the van when you have had enough` +
      ` — before the clock runs out.</div>` +
      `<div class="b-foot">DOORS OPEN SHORTLY</div></div>`;
    this.briefEl.classList.add('on');
  }

/**
 * The shift review.
 *
 * A bare table and a key prompt is a full stop. What actually makes somebody
 * press R is three numbers and a job title: what you took, what you left in
 * there, and whether it was your best night. The grade is the ladder — it is the
 * only thing in the game that remembers you across rounds, and it is written in
 * the mall's own management-speak because a heist game that hands you a medal is
 * a different game from this one.
 */
  showResults(lines: ScoreLine[], reason: string, you: string, night: ResultsExtras): void {
    const rows = lines
      .map((l) => {
        const me = l.id === you ? ' me' : '';
        return (
          `<tr class="${me}"><td class="n">${escapeHtml(l.name)}</td>` +
          `<td class="t">${l.team.toUpperCase()}</td>` +
          `<td class="s">${l.status.toUpperCase()}</td>` +
          `<td class="b">$${l.banked.toLocaleString()}</td>` +
          `<td class="u">${l.unbanked ? `lost $${l.unbanked.toLocaleString()}` : '—'}</td></tr>`
        );
      })
      .join('');
    const banked = lines.find((l) => l.id === you)?.banked ?? 0;
    const best = Math.max(night.best, banked);
    const tally =
      `<div class="r-tally">` +
      `<div><span>TAKEN</span><b class="good">$${banked.toLocaleString()}</b></div>` +
      `<div><span>LEFT IN THERE</span><b class="left">$${night.floor.toLocaleString()}</b></div>` +
      `<div><span>${night.newBest ? 'NEW PERSONAL BEST' : 'YOUR BEST'}</span>` +
      `<b class="${night.newBest ? 'good' : ''}">$${best.toLocaleString()}</b></div>` +
      `</div>` +
      `<div class="r-grade">${escapeHtml(grade(best))}</div>`;

    this.resultsEl.innerHTML =
      `<div class="r-card"><div class="r-head">MALL CLOSED</div>` +
      `<div class="r-sub">${escapeHtml(night.name)} \u00b7 ${escapeHtml(reason)}</div>` +
      `<table>${rows}</table>` +
      tally +
      `<div class="r-foot">PRESS R FOR A NEW NIGHT</div></div>`;
    this.resultsEl.classList.add('on');
  }

  hideResults(): void {
    this.resultsEl.classList.remove('on');
  }
}

/**
 * The ladder, in the mall's own words.
 *
 * Thresholds sit where they do because the whole building is about a hundred
 * and three thousand dollars and a full bag is roughly forty. Floor staff is one
 * good store. Keyholder is the jewellery shop cleared out. Regional director
 * means two full trips through a mall that spent the second one looking for you,
 * and it should stay out of reach for a long time.
 */
const GRADES: ReadonlyArray<[number, string]> = [
  [0, 'NO SHOW'],
  [1, 'CASUAL'],
  [2_500, 'SEASONAL HIRE'],
  [6_000, 'FLOOR STAFF'],
  [12_000, 'KEYHOLDER'],
  [20_000, 'DUTY MANAGER'],
  [32_000, 'STORE MANAGER'],
  [50_000, 'REGIONAL DIRECTOR'],
];

export function grade(banked: number): string {
  let name = GRADES[0][1];
  for (const [at, title] of GRADES) if (banked >= at) name = title;
  return name;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = cls;
  return n;
}

function mmss(t: number): string {
  const s = Math.max(0, Math.ceil(t));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

const PAUSE_CARD = `
  <div class="pause-card">
    <div class="pc-title">NIGHT SHIFT</div>
    <div class="pc-sub">CLICK TO TAKE CONTROL</div>
    <div class="pc-keys">
      <div><b>WASD</b> move</div><div><b>SHIFT</b> sprint</div>
      <div><b>CTRL</b> crouch</div><div><b>SPACE</b> jump</div>
      <div><b>E</b> hold to use</div><div><b>Q</b> drop everything</div>
      <div><b>F</b> use consumable</div><div><b>TAB</b> scores</div>
      <div><b>ESC</b> release mouse</div><div><b>R</b> restart round</div>
    </div>
    <div class="pc-note">Take what you can carry. Post it at a chute for 65%,
      or reach the loading dock for full price. The van in the car park ends your
      night. Weight is speed. The clock is the whole game.</div>
  </div>`;

let styled = false;
function injectStyle(): void {
  if (styled) return;
  styled = true;
  const s = document.createElement('style');
  s.textContent = CSS;
  document.head.appendChild(s);
}

/**
 * One string rather than a stylesheet file so the HUD is a single import with no
 * build-order dependency, and so the harness cannot photograph an unstyled frame
 * while a CSS request is in flight.
 */
const CSS = `
.hud{position:fixed;inset:0;pointer-events:none;user-select:none;
  font:11px/1.5 ui-monospace,"SF Mono",Menlo,monospace;letter-spacing:.16em;
  color:#cfd8e4;text-transform:uppercase;z-index:10}
.hud b{color:#eaf1fa;font-weight:600}

.hud-top{position:absolute;top:18px;left:50%;transform:translateX(-50%);text-align:center}
.hud-phase{font-size:9px;letter-spacing:.42em;color:#7f92a8}
.hud-clock{font-size:30px;letter-spacing:.10em;color:#dce6f2;font-variant-numeric:tabular-nums;
  text-shadow:0 0 18px rgba(120,170,220,.28)}
.hud-clock.warn{color:#ff8161;text-shadow:0 0 22px rgba(255,90,60,.45);animation:tick 1s steps(2) infinite}
@keyframes tick{50%{opacity:.55}}

.hud-money{position:absolute;top:20px;left:24px}
.hud-bank{font-size:21px;letter-spacing:.06em;color:#8ee6b4;font-variant-numeric:tabular-nums}
.hud-carry{font-size:9px;letter-spacing:.3em;color:#6d7f93;margin-top:2px}
.hud-carry.at-risk{color:#e8c07a}

.hud-centre{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
  width:96px;height:96px;display:grid;place-items:center}
.hud-reticle{width:5px;height:5px;border-radius:50%;background:#cfd8e4;opacity:.55;
  box-shadow:0 0 6px rgba(0,0,0,.9);transition:width .12s,height .12s,opacity .12s,background .12s}
.hud-reticle.active{width:11px;height:11px;opacity:.95;background:#8fd7ff;
  box-shadow:0 0 10px rgba(120,200,255,.7),0 0 3px rgba(0,0,0,.9)}
.hud-reticle.denied{width:9px;height:9px;background:#ff7a5c;opacity:.9}
.hud-ring{position:absolute;inset:0;opacity:0;transition:opacity .1s}
.hud-ring.on{opacity:1}
.hud-ring svg{width:100%;height:100%;transform:rotate(-90deg)}
.hud-ring circle{fill:none;stroke-width:3;stroke-linecap:butt}
.hud-ring .track{stroke:rgba(190,215,240,.14)}
.hud-ring .fill{stroke:#8fd7ff;stroke-dasharray:276.5;filter:drop-shadow(0 0 4px rgba(120,200,255,.6))}

.hud-prompt{position:absolute;top:calc(50% + 62px);left:50%;transform:translateX(-50%);
  text-align:center;opacity:0;transition:opacity .12s;max-width:60vw}
.hud-prompt.on{opacity:1}
.p-title{font-size:12px;letter-spacing:.28em;color:#eaf1fa}
.p-detail{font-size:10px;letter-spacing:.14em;color:#93a5b9;margin-top:3px;text-transform:none}
.p-verb{font-size:9px;letter-spacing:.34em;color:#8fd7ff;margin-top:7px}
.p-verb.blocked{color:#ff8161}

.hud-inventory{position:absolute;bottom:22px;left:24px;min-width:216px}
.hud-slots{display:flex;flex-direction:column;gap:2px;margin-bottom:8px}
.slot{display:flex;justify-content:space-between;gap:16px;font-size:10px;
  padding:2px 7px;background:rgba(12,18,26,.5);border-left:2px solid rgba(150,180,210,.3)}
.slot.tool{border-left-color:#8fd7ff}
.slot.consumable{border-left-color:#c9a0ff}
.slot .s-name{color:#c3d0de;text-transform:none;letter-spacing:.08em}
.slot .s-val{color:#8ee6b4;font-variant-numeric:tabular-nums}
.slot.empty{color:#55667a;background:none;border-left-color:transparent;padding-left:9px;font-size:9px;letter-spacing:.3em}
.hud-tools{display:flex;gap:6px}
.tool{font-size:9px;letter-spacing:.2em;padding:4px 9px;color:#0c1219;background:#8fd7ff}
.tool.empty{color:#55667a;background:rgba(12,18,26,.5);outline:1px solid rgba(120,150,180,.2)}

.hud-body{position:absolute;bottom:22px;right:24px;width:190px;display:flex;flex-direction:column;gap:6px}
.bar{position:relative;height:16px;background:rgba(12,18,26,.55);outline:1px solid rgba(120,150,180,.16)}
.bar-fill{position:absolute;inset:0 auto 0 0;width:0;background:rgba(140,190,235,.34);transition:width .1s linear}
.bar-fill.warm{background:rgba(232,192,122,.4)}
.bar-fill.hot{background:rgba(255,124,92,.44)}
.bar.stamina .bar-fill{background:rgba(142,230,180,.3)}
/* Empty is the good state here, so the empty part of the track is darker than
   the other two bars and the fill is a pale sodium wash — it reads as light
   spilling in from the left rather than as a meter filling up. */
.bar.shade{background:rgba(8,12,18,.66)}
.bar.shade .bar-fill{background:rgba(226,214,178,.26);transition:width .18s linear}
.bar-label{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
  font-size:8px;letter-spacing:.22em;color:#c3d0de}

.hud-voice{position:absolute;top:20px;right:24px;width:150px;display:none;
  flex-direction:column;align-items:flex-end;gap:4px}
.hud-voice.on{display:flex}
.v-mic{position:relative;width:100%;height:15px;background:rgba(12,18,26,.55);
  outline:1px solid rgba(120,150,180,.16)}
.v-level{position:absolute;inset:0 auto 0 0;width:0;background:rgba(142,230,180,.34);
  transition:width .06s linear}
.v-mic.off .v-level,.v-mic.muted .v-level{background:rgba(255,124,92,.24)}
.v-label{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
  font-size:8px;letter-spacing:.22em;color:#8d9db0}
.v-mic.live .v-label{color:#a9f0c8}
.v-mic.muted .v-label{color:#ffb09a}
.v-who{font-size:9px;letter-spacing:.24em;color:#8fd7ff;padding:1px 7px;
  background:rgba(10,15,22,.62);border-right:2px solid #8fd7ff;animation:slide-r .14s ease-out}
@keyframes slide-r{from{opacity:0;transform:translateX(10px)}}

.hud-detect{position:absolute;top:50%;left:50%;transform:translate(-50%,-118px);
  font-size:10px;letter-spacing:.5em;color:#ff8161;opacity:0;transition:opacity .15s}
.hud-detect.caught{animation:tick .5s steps(2) infinite}

.hud-log{position:absolute;bottom:130px;left:24px;display:flex;flex-direction:column;gap:3px;max-width:40vw}
.toast{font-size:10px;letter-spacing:.14em;padding:3px 9px;background:rgba(10,15,22,.62);
  border-left:2px solid #6d7f93;color:#c3d0de;text-transform:none;
  animation:slide .18s ease-out;transition:opacity .4s,transform .4s}
.toast.good{border-left-color:#8ee6b4;color:#a9f0c8}
.toast.bad{border-left-color:#ff7a5c;color:#ffb09a}
.toast.gone{opacity:0;transform:translateX(-12px)}
@keyframes slide{from{opacity:0;transform:translateX(-10px)}}

.hud-pause,.hud-results{position:absolute;inset:0;display:grid;place-items:center;
  background:rgba(5,8,12,.72);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s}
.hud-pause.on,.hud-results.on{opacity:1}
.pause-card,.r-card{text-align:center;max-width:520px;padding:34px 42px;
  background:rgba(9,13,19,.86);outline:1px solid rgba(140,175,210,.18)}
.pc-title{font-size:24px;letter-spacing:.52em;color:#eaf1fa}
.pc-sub{font-size:9px;letter-spacing:.42em;color:#8fd7ff;margin-top:10px}
.pc-keys{display:grid;grid-template-columns:1fr 1fr;gap:4px 26px;margin:22px 0 18px;
  font-size:9px;letter-spacing:.14em;color:#93a5b9;text-align:left}
.pc-note{font-size:10px;line-height:1.7;color:#7f92a8;text-transform:none;letter-spacing:.04em}

.hud-results table{border-collapse:collapse;margin:20px 0 6px;width:100%;font-size:10px}
.hud-results td{padding:5px 10px;border-top:1px solid rgba(140,175,210,.12);text-align:left}
.hud-results tr.me td{color:#eaf1fa;background:rgba(143,215,255,.07)}
.hud-results td.b{color:#8ee6b4;text-align:right;font-variant-numeric:tabular-nums}
.hud-results td.u{color:#a2705f;text-align:right;text-transform:none}
.hud-results td.t,.hud-results td.s{color:#7f92a8}
.r-head{font-size:22px;letter-spacing:.46em;color:#eaf1fa}
.r-sub{font-size:9px;letter-spacing:.36em;color:#8fd7ff;margin-top:8px}
.r-foot{font-size:9px;letter-spacing:.36em;color:#7f92a8;margin-top:14px}
.r-tally{display:grid;gap:5px;margin:18px 0 0;font-size:9px;letter-spacing:.22em}
.r-tally div{display:flex;justify-content:space-between;gap:30px}
.r-tally span{color:#7f92a8}
.r-tally b{font-weight:400;color:#c8d6e6;font-variant-numeric:tabular-nums}
.r-tally b.good{color:#8ee6b4}
.r-tally b.left{color:#a2705f}
.r-grade{margin-top:16px;padding-top:14px;border-top:1px solid rgba(140,175,210,.14);
  font-size:13px;letter-spacing:.34em;color:#8fd7ff}

/* The briefing card sits low and off to one side. It is information, not a
   modal: you should be able to walk to the bench and pick a tool while reading
   it, and a full-screen scrim during the only phase with anything to do in it
   would be the game getting in its own way. */
.hud-brief{position:absolute;left:50%;bottom:16%;transform:translateX(-50%);
  opacity:0;transition:opacity .5s;pointer-events:none}
.hud-brief.on{opacity:1}
/* Except when the game is asking for the mouse. The pause card is centred and
   tall enough to reach the briefing's top edge on a laptop, so the very first
   screen of a new game was the controls list printed straight through the
   store name. One card at a time: read the keys, click in, then read the job.
   The rule itself lives below the prompt rule, not here — see there. */
.b-card{text-align:center;padding:18px 34px;background:rgba(9,13,19,.72);
  outline:1px solid rgba(140,175,210,.16);animation:slide .5s ease both}
.b-head{font-size:8px;letter-spacing:.5em;color:#7f92a8}
.b-name{font-size:26px;letter-spacing:.4em;color:#eaf1fa;margin:4px 0 12px}
.b-label{font-size:9px;letter-spacing:.34em;color:#8fd7ff}
.b-where{font-size:13px;letter-spacing:.3em;color:#eaf1fa;margin-top:6px}
.b-what{font-size:10px;letter-spacing:.16em;color:#93a5b9;margin-top:6px}
.b-what b{color:#8ee6b4;font-weight:400}
.b-foot{font-size:8px;letter-spacing:.4em;color:#5f7186;margin-top:14px}
/* The one sentence on the card that is not in character. The mall talks like a
   mall everywhere else; here the game tells you what a consignment is for. */
.b-plain{font-size:9px;letter-spacing:.22em;color:#8ea3ba;margin-top:12px;
  padding-top:12px;border-top:1px solid rgba(140,175,210,.14);text-transform:none;
  max-width:64ch;text-wrap:balance}
/* And it hides for the same reason the pause card does. The card is tall enough
   to reach the interaction prompt, so looking at a tool on the bench — which is
   what the card has just told you to do — printed CROWBAR straight through the
   consignment. One card at a time; step back from the bench and it returns. */
.hud-prompt.on ~ .hud-brief.on{opacity:.12}
/* Pause and results hide the briefing outright, and this has to be declared
   AFTER the prompt rule above. Both are one class plus one class, so they carry
   identical specificity and source order is the only tie-break — with the hide
   rule written first, the dim-to-12% rule won on the pause screen and the
   briefing printed faintly through the controls list, which is precisely what
   it was written to prevent. It is the first screen of every new game. */
.hud-pause.on ~ .hud-brief.on,.hud-results.on ~ .hud-brief.on{opacity:0}

/* The objective sits directly under the clock because that is already where the
   eye goes. Two weights: the verb is the instruction, the detail is the reason,
   and the detail is dim enough that a player who knows the game stops reading
   it after the first night while the verb stays legible at a glance forever. */
.hud-objective{margin-top:7px;opacity:0;transition:opacity .35s}
.hud-objective.on{opacity:1}
.o-verb{font-size:12px;letter-spacing:.34em;color:#8fd7ff;
  text-shadow:0 0 16px rgba(80,160,220,.3)}
.o-verb.urgent{color:#ff8161;text-shadow:0 0 18px rgba(255,90,60,.4)}
/* One line, always. Wrapped, the second line was usually two words orphaned
   under a centred first line, which reads as the HUD having broken rather than
   as a sentence. Released back to wrapping only on a window too narrow to hold
   it, where clipping would be worse. */
.o-detail{font-size:9px;letter-spacing:.16em;color:#7f92a8;margin-top:3px;
  text-transform:none;white-space:nowrap}
@media (max-width:820px){.o-detail{white-space:normal;max-width:52ch}}
.hud-pause.on ~ .hud-mark{opacity:0}

/* The waypoint. Hairline, unfilled, no animation: it is on screen for the whole
   round and anything with a pulse would become the thing you watch. */
.hud-mark{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  opacity:0;transition:opacity .3s;text-align:center;pointer-events:none}
.hud-mark.on{opacity:.9}
.m-pip{width:9px;height:9px;margin:0 auto;border:1px solid rgba(150,215,255,.85);
  transform:rotate(45deg);box-shadow:0 0 12px rgba(80,170,230,.45)}
/* Backed, not just shadowed. The mall is full of its own cyan signage and the
   waypoint kept landing on top of the directory board, where two lots of glowing
   monospace at the same size are simply illegible over each other. */
.m-text{font-size:8px;letter-spacing:.26em;color:#9fc4e0;margin-top:7px;
  padding:2px 6px;background:rgba(7,11,17,.7);white-space:nowrap;
  text-shadow:0 1px 3px rgba(0,0,0,.9)}
/* Off screen: the diamond goes and an arrow takes its place, so the marker
   never lies about the objective being in front of you. */
.m-arrow{display:none;width:0;height:0;margin:0 auto;
  border:9px solid transparent;border-left-color:rgba(160,220,255,.95);
  filter:drop-shadow(0 1px 2px rgba(0,0,0,.9))}
.hud-mark.edge .m-arrow{display:block}
.hud-mark.edge .m-pip{display:none}
.hud-mark.edge .m-text{margin-top:4px}
`;
